import React, { FormEvent, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogDescription, DialogPanel, DialogTitle } from '@headlessui/react';
import RefineReportButton, { RefineResult } from './RefineReportButton';
import ExistingReportChoice from './ExistingReportChoice';
import { ReportIntent, ReportSubmitError } from '@/app/lib/reportIntent';
import { FeedbackSelection } from './FeedbackAreaPicker';

export interface FeedbackRefineContext {
  fault_domain?: string | null;
  ai_category?: string | null;
  intent?: ReportIntent;
}

export interface FeedbackSubmitResult {
  reference?: string;
  message: string;
}

interface ProblemReportOverlayProps {
  isOpen: boolean;
  preserveDraftWhenClosed?: boolean;
  onClose: () => void;
  onRedoArea: () => void;
  onRemoveScreenshot: () => void;
  screenshot: string;
  selectedArea: FeedbackSelection;
  screenshotSize: { width: number; height: number };
  onSubmit: (description: string, screenshot: string, url: string, selectedArea: string,
             cc_me: boolean, refine?: FeedbackRefineContext) => Promise<FeedbackSubmitResult>;
}

type ProblemReportContentProps = Omit<ProblemReportOverlayProps, 'preserveDraftWhenClosed'>;

const ProblemReportContent: React.FC<ProblemReportContentProps> = ({
  isOpen,
  onClose,
  onRedoArea,
  onRemoveScreenshot,
  screenshot,
  selectedArea,
  screenshotSize,
  onSubmit,
}) => {
  const [intent, setIntent] = useState<ReportIntent>({ new_issue: true });
  const [retryOnly, setRetryOnly] = useState(false);
  const [description, setDescription] = useState('');
  const [cc_me, setCCMe] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [refineResult, setRefineResult] = useState<RefineResult | null>(null);
  const [descriptionError, setDescriptionError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<FeedbackSubmitResult | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const resetDraft = () => {
    setIntent({ new_issue: true });
    setRetryOnly(false);
    setDescription('');
    setCCMe(false);
    setIncludeScreenshot(true);
    setRefineResult(null);
    setDescriptionError('');
    setSubmitError('');
    setIsSubmitting(false);
    setSubmitResult(null);
  };

  const handleClose = () => {
    if (isSubmitting || retryOnly) return;
    resetDraft();
    onClose();
  };

  const handleRedoArea = () => {
    if (isSubmitting || retryOnly) return;
    setRefineResult(null);
    setSubmitError('');
    onRedoArea();
  };

  const handleRemoveScreenshot = () => {
    setIncludeScreenshot(false);
    onRemoveScreenshot();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    const cleanedDescription = description.trim();
    if (!cleanedDescription) {
      setDescriptionError('Describe what went wrong before submitting.');
      descriptionRef.current?.focus();
      return;
    }

    if (intent.follow_up_of && includeScreenshot && screenshot) {
      setSubmitError('Choose a new report to include your screenshot, or untick the screenshot to add text only.');
      return;
    }
    setDescriptionError('');
    setSubmitError('');
    setIsSubmitting(true);
    try {
      const result = await onSubmit(
        cleanedDescription,
        includeScreenshot ? screenshot : '',
        window.location.href,
        selectedArea.description,
        intent.follow_up_of ? false : cc_me,
        { fault_domain: refineResult?.fault_domain, ai_category: refineResult?.category, intent },
      );
      setRetryOnly(false);
      setSubmitResult(result);
      setRefineResult(null);
    } catch (error) {
      setRetryOnly(error instanceof ReportSubmitError && error.retryOnly);
      setSubmitError(`${error instanceof Error ? error.message : 'Your report could not be sent.'} Your draft is still here.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const markerStyle = screenshot && screenshotSize.width > 0 && screenshotSize.height > 0
    ? {
        left: `${(selectedArea.rect.x / screenshotSize.width) * 100}%`,
        top: `${(selectedArea.rect.y / screenshotSize.height) * 100}%`,
        width: `${(selectedArea.rect.width / screenshotSize.width) * 100}%`,
        height: `${(selectedArea.rect.height / screenshotSize.height) * 100}%`,
      }
    : undefined;

  if (!isOpen) return null;

  return (
    <Dialog open onClose={handleClose} initialFocus={descriptionRef} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 dark:bg-black/70" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-8">
        <div className="flex min-h-full items-start justify-center">
          <DialogPanel className="w-full max-w-lg rounded-lg border border-border bg-white p-5 shadow-xl dark:bg-gray-800 sm:p-6">
            {submitResult ? (
              <div aria-live="polite">
                <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">Feedback sent</DialogTitle>
                <DialogDescription className="mt-3 text-gray-700 dark:text-gray-300">{submitResult.message}</DialogDescription>
                {submitResult.reference && (
                  <>
                    <p className="mt-4 rounded-lg border border-green-300 bg-green-50 p-3 text-green-900 dark:border-green-700 dark:bg-green-900/30 dark:text-green-100">
                      Support reference: <strong>{submitResult.reference}</strong>
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">Quote this reference if you contact your support provider.</p>
                  </>
                )}
                <div className="mt-6 flex justify-end">
                  <button type="button" onClick={handleClose} className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">Close</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">Send feedback</DialogTitle>
                <DialogDescription className="mt-1 text-sm text-muted-foreground">
                  Tell us what happened. The selected control and optional screenshot help support find the right place.
                </DialogDescription>

                <ExistingReportChoice open={isOpen} intent={intent} onChange={setIntent} screenshot={includeScreenshot && !!screenshot} disabled={isSubmitting || retryOnly} />
                <section aria-labelledby="selected-area-heading" className="mt-5 rounded-lg border border-border bg-gray-50 p-3 dark:bg-gray-900/40">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 id="selected-area-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">Selected area</h3>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{selectedArea.description}</p>
                    </div>
                    <button type="button" onClick={handleRedoArea} disabled={isSubmitting || retryOnly} aria-label="Redo selected area" className="shrink-0 rounded px-2 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-blue-300">Redo</button>
                  </div>

                  {screenshot ? (
                    <div className="mt-3">
                      <div className="max-h-56 overflow-auto rounded border border-border bg-black">
                        <div className="relative">
                          {/* A data-URL capture cannot benefit from Next image optimisation. */}
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={screenshot} alt={`Screenshot preview with ${selectedArea.description} selected`} className="block h-auto w-full" />
                          {markerStyle && <span aria-hidden="true" className="absolute min-h-1 min-w-1 border-2 border-red-500 bg-red-500/20 shadow-[0_0_0_1px_white]" style={markerStyle} />}
                        </div>
                      </div>
                      <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={includeScreenshot} onChange={(event) => setIncludeScreenshot(event.target.checked)} disabled={isSubmitting || retryOnly} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600" />
                        <span><strong>Attach this screenshot.</strong> It may contain site information visible on screen; untick to omit it.</span>
                      </label>
                      <button type="button" onClick={handleRemoveScreenshot} disabled={isSubmitting || retryOnly} className="mt-2 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-300">Remove screenshot</button>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">Screenshot removed. The selected control description will still be sent.</p>
                  )}
                </section>

                <div className="mt-5">
                  <label htmlFor="feedback-description" className="block text-sm font-medium text-gray-900 dark:text-gray-100">What went wrong? <span aria-hidden="true">*</span></label>
                  <textarea
                    ref={descriptionRef}
                    id="feedback-description"
                    value={description}
                    disabled={isSubmitting || retryOnly}
                    onChange={(event) => {
                      setDescription(event.target.value);
                      setDescriptionError('');
                      setSubmitError('');
                      setRefineResult(null);
                    }}
                    aria-required="true"
                    aria-invalid={Boolean(descriptionError)}
                    aria-describedby={descriptionError ? 'feedback-description-error' : undefined}
                    placeholder="Describe what you expected and what happened..."
                    className="mt-1 h-32 w-full rounded border border-gray-300 bg-white p-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                  {descriptionError && <p id="feedback-description-error" role="alert" className="mt-1 text-sm text-red-700 dark:text-red-300">{descriptionError}</p>}
                </div>

                <RefineReportButton
                  description={description}
                  disabled={isSubmitting || retryOnly}
                  buildPayload={() => ({ description, context: 'feedback', selectedArea: selectedArea.description, url: typeof window !== 'undefined' ? window.location.href : '' })}
                  onApply={setDescription}
                  onResult={setRefineResult}
                />

                {intent.follow_up_of ? <p className="my-3 text-sm text-muted-foreground">This adds evidence to the existing case. It does not send a new report or an email copy.</p> : (<fieldset className="mb-4 rounded-lg border border-gray-200 p-3 dark:border-gray-600">
                  <legend className="px-1 text-sm font-medium text-gray-900 dark:text-gray-100">Case notifications</legend>
                  <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" name="feedback_notifications" checked={!cc_me} onChange={() => setCCMe(false)} disabled={isSubmitting || retryOnly} className="mt-0.5 h-4 w-4 text-blue-600" />
                    <span>In-app status only</span>
                  </label>
                  <label className="mt-2 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="radio" name="feedback_notifications" checked={cc_me} onChange={() => setCCMe(true)} disabled={isSubmitting || retryOnly} className="mt-0.5 h-4 w-4 text-blue-600" />
                    <span>In-app status and an email copy</span>
                  </label>
                </fieldset>)}

                {submitError && <p role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200">{submitError}</p>}
                <div className="flex justify-end space-x-2">
                  <button type="button" onClick={handleClose} disabled={isSubmitting || retryOnly} className="rounded border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-300">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">{isSubmitting ? 'Sending…' : retryOnly ? 'Retry same submission' : intent.follow_up_of ? 'Add evidence to selected case' : 'Submit new report'}</button>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const ProblemReportOverlay: React.FC<ProblemReportOverlayProps> = ({
  isOpen,
  preserveDraftWhenClosed = false,
  ...props
}) => {
  if (!isOpen && !preserveDraftWhenClosed) return null;
  return <ProblemReportContent isOpen={isOpen} {...props} />;
};

export default ProblemReportOverlay;
